import { ToastContext } from "context/ToastContext";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { ProgressSpinner } from "primereact/progressspinner";
import { useContext, useState } from "react";
import {
  useAthenaImportMutation,
  useLazyAthenaImportStatusQuery,
  IAthenaImportResult,
} from "store/queries/integration";
import styled from "styled-components";

import { AthenaImportResults, FileInfo } from "./AthenaImportResults";

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(4)};
  min-width: 500px;
`;

const FileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(2)};
`;

const FileLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
`;

const SelectedFileDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${sizer(2)};
  background-color: var(--surface-ground);
  border-radius: 4px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${sizer(8)};
  gap: ${sizer(2)};
`;

interface IAthenaImportDialogProps {
  visible: boolean;
  onHide: () => void;
  onSuccess?: () => void;
  /**
   * CLE-2948 — the account the dialog was opened from. Recorded on the import so Integrations → Athena
   * can show whose import it was; appointments are still routed to accounts by the server.
   */
  accountId?: string;
}

export function AthenaImportDialog({
  visible,
  onHide,
  onSuccess,
  accountId,
}: IAthenaImportDialogProps) {
  const { current: toastElement } = useContext(ToastContext);
  const [athenaFile, setAthenaFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<IAthenaImportResult | null>(
    null
  );
  // CLE-2633 — the import runs server-side now, so the dialog polls rather than awaits.
  const [isPolling, setIsPolling] = useState(false);
  const [fetchStatus] = useLazyAthenaImportStatusQuery();

  const [athenaImport, { isLoading }] = useAthenaImportMutation();

  const handleFileSelect = (e: { files: File[] }) => {
    if (e.files && e.files.length > 0) {
      setAthenaFile(e.files[0]);
    }
  };

  /**
   * Poll the import row until it reaches a terminal status.
   *
   * There is no client-side timeout: a large report legitimately takes minutes, and an import that
   * dies with its container is swept to `failed` by a server-side cron, so polling always ends.
   */
  const pollUntilFinished = async (importId: string) => {
    const intervalMs = 3000;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await fetchStatus(importId).unwrap();
      if (data.status === "completed" || data.status === "failed") return data;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, intervalMs);
      });
    }
  };

  const handleImport = async () => {
    if (!athenaFile) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select a file before importing",
      });
      return;
    }

    try {
      // The upload answers 202 with an id; the work continues server-side. Aptible's nginx closes
      // the connection at 60s, so awaiting the whole import here is what produced the 504.
      const started = await athenaImport({
        athenaReport: athenaFile,
        accountId,
      }).unwrap();

      setIsPolling(true);
      const result = { data: await pollUntilFinished(started.data.importId) };
      setIsPolling(false);

      if (result.data.status === "failed") {
        toastElement?.show({
          severity: "error",
          summary: "Import Failed",
          detail: result.data.errorMessage || "An error occurred during import",
        });
        return;
      }

      setImportResult(result.data);

      if (result.data.successCount > 0 || result.data.updateCount > 0) {
        const parts = [];
        if (result.data.successCount > 0) {
          parts.push(`created ${result.data.successCount}`);
        }
        if (result.data.updateCount > 0) {
          parts.push(`updated ${result.data.updateCount}`);
        }
        toastElement?.show({
          severity: "success",
          summary: "Import Complete",
          detail: `Successfully ${parts.join(" and ")} estimates`,
        });
        onSuccess?.();
      }

      if (result.data.errorCount > 0 || result.data.unmappedPayerCount > 0) {
        const errorParts = [];
        if (result.data.errorCount > 0) {
          errorParts.push(`${result.data.errorCount} errors`);
        }
        if (result.data.unmappedPayerCount > 0) {
          errorParts.push(`${result.data.unmappedPayerCount} unmapped payers`);
        }
        toastElement?.show({
          severity: "warn",
          summary: "Import Completed with Issues",
          detail: errorParts.join(", "),
        });
      }
    } catch (error: any) {
      setIsPolling(false);
      toastElement?.show({
        severity: "error",
        summary: "Import Failed",
        detail: error.data?.message || "An error occurred during import",
      });
    }
  };

  const handleClose = () => {
    setAthenaFile(null);
    setImportResult(null);
    onHide();
  };

  const canImport = athenaFile && !isLoading && !isPolling;

  const footerContent = (
    <div className="flex justify-content-end gap-2">
      <Button
        label={importResult ? "Close" : "Cancel"}
        onClick={handleClose}
        className="p-button-text"
      />
      {!importResult && (
        <Button
          label="Import"
          onClick={handleImport}
          disabled={!canImport}
          loading={isLoading || isPolling}
        />
      )}
    </div>
  );

  const renderDialogContent = () => {
    if (isLoading || isPolling) {
      return (
        <LoadingContainer>
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          <span>
            Importing estimates. A large report can take a few minutes — you can
            leave this open.
          </span>
        </LoadingContainer>
      );
    }

    if (importResult) {
      return <AthenaImportResults importResult={importResult} />;
    }

    return (
      <DialogContent>
        <FileSection>
          <FileLabel>Athena Scheduling Report</FileLabel>
          <FileInfo>
            Upload the Clear Health Reporting file from Athena (.xlsx or .csv)
          </FileInfo>
          {athenaFile ? (
            <SelectedFileDisplay>
              <span>{athenaFile.name}</span>
              <Button
                icon="pi pi-times"
                className="p-button-text p-button-sm"
                onClick={() => setAthenaFile(null)}
              />
            </SelectedFileDisplay>
          ) : (
            <FileUpload
              mode="basic"
              accept=".csv,.xlsx"
              maxFileSize={10000000}
              customUpload
              auto
              uploadHandler={handleFileSelect}
              chooseLabel="Select File"
            />
          )}
        </FileSection>
      </DialogContent>
    );
  };

  return (
    <Dialog
      header="Import Estimates from Athena"
      visible={visible}
      onHide={handleClose}
      footer={footerContent}
      style={{ width: importResult ? "700px" : "auto" }}
    >
      {renderDialogContent()}
    </Dialog>
  );
}
